import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Typography, Button, Box } from '@mui/material';
import axios from 'axios';
import Swal from 'sweetalert2';
import UploadIcon from '@mui/icons-material/Upload';
import DescriptionIcon from '@mui/icons-material/Description';
import NewTransaction from './NewTransaction';
import ExistingTransaction from './ExistingTransaction';
import './ViewAndUpload.css';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
if (!API_BASE_URL) {
  throw new Error('API_BASE_URL is not defined in .env');
}

class ErrorBoundary extends React.Component {
  state = { hasError: false, error: null };

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('ErrorBoundary caught:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <Typography color="error">
          Something went wrong: {this.state.error?.message || 'Unknown error'}
        </Typography>
      );
    }
    return this.props.children;
  }
}

const ViewAndUpload = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [selectedCategory, setSelectedCategory] = useState('');
  const [userRole, setUserRole] = useState('normal');
  const [transactions, setTransactions] = useState([]);
  const [selectedTransaction, setSelectedTransaction] = useState('');
  const [selectedFileIndex, setSelectedFileIndex] = useState(null);
  const [selectedFileType, setSelectedFileType] = useState(null);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [extractedData, setExtractedData] = useState(null);
  const [originalExtractedData, setOriginalExtractedData] = useState(null);
  const [documentId, setDocumentId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [pollingProgress, setPollingProgress] = useState(0);
  const [pollingReferenceIds, setPollingReferenceIds] = useState(new Set());
  const [progressComplete, setProgressComplete] = useState(false);
  const [docType, setDocType] = useState('Commercial Invoice');
  const [isDataChanged, setIsDataChanged] = useState(false);

  const fetchThumbnail = async (filename) => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) throw new Error('No authentication token found.');
      const response = await axios.get(`${API_BASE_URL}/thumbnails/${filename}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });
      return URL.createObjectURL(response.data);
    } catch (error) {
      console.error('Error fetching thumbnail from /thumbnails:', error);
      return '/default_thumb.jpg';
    }
  };

  const getThumbnailFilename = (pdfPath, thumbnailPath) => {
    if (thumbnailPath) return thumbnailPath;
    if (pdfPath) {
      const fileName = pdfPath.split('/storage/')[1] || pdfPath.split('/').pop();
      return fileName.replace(/\.pdf$/, '.jpg');
    }
    return null;
  };

  const truncateFileName = (name, maxLength = 25) => {
    if (name.length <= maxLength) return name;
    return name.substring(0, maxLength - 3) + '...';
  };

  const handleCategoryChange = async (category, preselectedTransactionId = null, preselectedDocumentId = null) => {
    setSelectedCategory(category);
    setSelectedTransaction('');
    setSelectedFileIndex(null);
    setSelectedFileType(null);
    setPdfUrl(null);
    setExtractedData(null);
    setOriginalExtractedData(null);
    setDocumentId(null);
    setLoading(false);
    setError(null);
    setUploadedFiles([]);
    setPollingReferenceIds(new Set());
    setPollingProgress(0);
    setProgressComplete(false);
    setDocType('Commercial Invoice');
    setIsDataChanged(false);

    if (category === 'Existing Transaction') {
      try {
        const token = localStorage.getItem('authToken');
        if (!token) {
          navigate('/login');
          throw new Error('No authentication token found.');
        }
        const response = await axios.get(`${API_BASE_URL}/transactions`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const transactionsWithThumbnails = await Promise.all(
          response.data.map(async (transaction) => {
            let thumbnail = '/default_thumb.jpg';
            if (transaction.documents && transaction.documents.length > 0) {
              const pdfPath = transaction.documents[0].pdf_path;
              const filename = getThumbnailFilename(pdfPath, null);
              if (filename) thumbnail = await fetchThumbnail(filename);
            }
            return {
              id: transaction.id.toString(),
              name: transaction.name || `Transaction ${transaction.id}`,
              thumbnail,
              files: await Promise.all(
                transaction.documents.map(async (doc) => {
                  const filename = getThumbnailFilename(doc.pdf_path, null);
                  const thumbnail = filename ? await fetchThumbnail(filename) : '/default_thumb.jpg';
                  return {
                    id: doc.id.toString(),
                    name: doc.pdf_file_name || doc.pdf_path.split('/').pop(),
                    docType: doc.type || 'Unknown',
                    pdfUrl: null,
                    fileData: null,
                    extractedData: null,
                    pdfPath: doc.pdf_path,
                    thumbnail,
                    transaction_set_id: doc.transaction_set_id.toString(),
                    isSaved: true,
                  };
                })
              ),
            };
          })
        );
        setTransactions(transactionsWithThumbnails);
        localStorage.setItem('transactions', JSON.stringify(transactionsWithThumbnails));

        if (preselectedTransactionId && preselectedDocumentId) {
          const transaction = transactionsWithThumbnails.find(t => t.id === preselectedTransactionId);
          if (transaction) {
            setSelectedTransaction(preselectedTransactionId);
            const fileIndex = transaction.files.findIndex(f => f.id === preselectedDocumentId);
            if (fileIndex !== -1) {
              setSelectedFileIndex(fileIndex);
              setSelectedFileType('existing');
            } else {
              Swal.fire({
                icon: 'error',
                title: 'File Not Found',
                text: 'The specified file could not be found in the transaction.',
              });
            }
          } else {
            Swal.fire({
              icon: 'error',
              title: 'Transaction Not Found',
              text: 'The specified transaction could not be found.',
            });
          }
        }
      } catch (error) {
        let errorMessage = 'Unable to fetch transactions: ' + (error.response?.data?.message || error.message || 'Unknown error');
        if (error.response?.status === 403) {
          errorMessage = 'Access denied: Invalid or expired token. Please log in again.';
          localStorage.removeItem('authToken');
          navigate('/login');
        }
        Swal.fire({
          icon: 'error',
          title: 'Failed to Load Transactions',
          text: errorMessage,
        });
      }
    } else {
      setTransactions([]);
    }
  };

  const handleSaveDocument = (file, index) => {
    let updatedTransactions = [...transactions];
    const newFile = {
      id: file.documentId,
      name: file.name,
      docType: file.docType,
      pdfUrl: file.pdfUrl,
      fileData: file.fileData,
      extractedData: file.initialData,
      pdfPath: file.pdfPath,
      thumbnail: file.thumbnail,
      transaction_set_id: file.transaction_set_id,
      isSaved: true,
    };
    const transactionIndex = updatedTransactions.findIndex(t => t.id === file.transaction_set_id);
    if (transactionIndex !== -1) {
      updatedTransactions[transactionIndex] = {
        ...updatedTransactions[transactionIndex],
        files: [...updatedTransactions[transactionIndex].files, newFile],
        thumbnail: updatedTransactions[transactionIndex].thumbnail || newFile.thumbnail || '/default_thumb.jpg',
      };
    } else {
      const newTransaction = {
        id: file.transaction_set_id.toString(),
        name: `Transaction ${transactions.length + 1}`,
        thumbnail: newFile.thumbnail || '/default_thumb.jpg',
        files: [newFile],
      };
      updatedTransactions = [...updatedTransactions, newTransaction];
    }
    setTransactions(updatedTransactions);
    localStorage.setItem('transactions', JSON.stringify(updatedTransactions));
  };

  const handleCompleteTransaction = () => {
    setSelectedCategory('');
    setTransactions([]);
    setSelectedTransaction('');
    setSelectedFileIndex(null);
    setSelectedFileType(null);
    setPdfUrl(null);
    setExtractedData(null);
    setOriginalExtractedData(null);
    setDocumentId(null);
    setPollingReferenceIds(new Set());
    setPollingProgress(0);
    setProgressComplete(false);
    setIsDataChanged(false);
  };

  useEffect(() => {
    const fetchUserRole = async () => {
      try {
        const token = localStorage.getItem('authToken');
        if (!token) throw new Error('No authentication token found.');
        const response = await axios.get(`${API_BASE_URL}/users/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const isSuperuser = response.data.is_superuser === 'True';
        setUserRole(isSuperuser ? 'admin' : 'normal');
      } catch (err) {
        setUserRole('normal');
        Swal.fire({
          icon: 'error',
          title: 'Failed to Fetch User Role',
          text: err.message || 'Unable to fetch user role.',
        });
      }
    };
    fetchUserRole();
  }, []);

  useEffect(() => {
    if (location.state?.transactionId) {
      setSelectedCategory('Existing Transaction');
      if (location.state.documentId) {
        handleCategoryChange('Existing Transaction', location.state.transactionId, location.state.documentId);
      }
    }
  }, [location.state]);

  return (
    <ErrorBoundary>
      <div className="view-main">

        <Box className="category-buttons">
          <Button
            variant={selectedCategory === 'New Transaction' ? 'contained' : 'outlined'}
            onClick={() => handleCategoryChange('New Transaction')}
            startIcon={<UploadIcon />}
          >
            New Transaction
          </Button>
          <Button
            variant={selectedCategory === 'Existing Transaction' ? 'contained' : 'outlined'}
            onClick={() => handleCategoryChange('Existing Transaction')}
            startIcon={<DescriptionIcon />}
          >
            Existing Transaction
          </Button>
        </Box>
        
        {/* Show simplified new transaction interface when no category is selected */}
        {selectedCategory === '' && (
          <NewTransaction
            userRole={userRole}
            onSaveDocument={handleSaveDocument}
            onCompleteTransaction={handleCompleteTransaction}
            onCategoryChange={setSelectedCategory}
            uploadedFiles={uploadedFiles}
            setUploadedFiles={setUploadedFiles}
            selectedFileIndex={selectedFileIndex}
            setSelectedFileIndex={setSelectedFileIndex}
            selectedFileType={selectedFileType}
            setSelectedFileType={setSelectedFileType}
            pdfUrl={pdfUrl}
            setPdfUrl={setPdfUrl}
            extractedData={extractedData}
            setExtractedData={setExtractedData}
            originalExtractedData={originalExtractedData}
            setOriginalExtractedData={setOriginalExtractedData}
            documentId={documentId}
            setDocumentId={setDocumentId}
            loading={loading}
            setLoading={setLoading}
            error={error}
            setError={setError}
            pollingProgress={pollingProgress}
            setPollingProgress={setPollingProgress}
            pollingReferenceIds={pollingReferenceIds}
            setPollingReferenceIds={setPollingReferenceIds}
            progressComplete={progressComplete}
            setProgressComplete={setProgressComplete}
            docType={docType}
            setDocType={setDocType}
            isDataChanged={isDataChanged}
            setIsDataChanged={setIsDataChanged}
            selectedCategory={selectedCategory}
            isSimplifiedMode={true}
          />
        )}
        
        {/* Show full new transaction interface when 'New Transaction' is selected */}
        {selectedCategory === 'New Transaction' && (
          <NewTransaction
            userRole={userRole}
            onSaveDocument={handleSaveDocument}
            onCompleteTransaction={handleCompleteTransaction}
            onCategoryChange={setSelectedCategory}
            uploadedFiles={uploadedFiles}
            setUploadedFiles={setUploadedFiles}
            selectedFileIndex={selectedFileIndex}
            setSelectedFileIndex={setSelectedFileIndex}
            selectedFileType={selectedFileType}
            setSelectedFileType={setSelectedFileType}
            pdfUrl={pdfUrl}
            setPdfUrl={setPdfUrl}
            extractedData={extractedData}
            setExtractedData={setExtractedData}
            originalExtractedData={originalExtractedData}
            setOriginalExtractedData={setOriginalExtractedData}
            documentId={documentId}
            setDocumentId={setDocumentId}
            loading={loading}
            setLoading={setLoading}
            error={error}
            setError={setError}
            pollingProgress={pollingProgress}
            setPollingProgress={setPollingProgress}
            pollingReferenceIds={pollingReferenceIds}
            setPollingReferenceIds={setPollingReferenceIds}
            progressComplete={progressComplete}
            setProgressComplete={setProgressComplete}
            docType={docType}
            setDocType={setDocType}
            isDataChanged={isDataChanged}
            setIsDataChanged={setIsDataChanged}
            selectedCategory={selectedCategory}
            isSimplifiedMode={false}
          />
        )}
        
        {/* Show existing transaction interface when 'Existing Transaction' is selected */}
        {selectedCategory === 'Existing Transaction' && (
          <ExistingTransaction
            userRole={userRole}
            transactions={transactions}
            setTransactions={setTransactions}
            selectedTransaction={selectedTransaction}
            setSelectedTransaction={setSelectedTransaction}
            selectedFileIndex={selectedFileIndex}
            setSelectedFileIndex={setSelectedFileIndex}
            selectedFileType={selectedFileType}
            setSelectedFileType={setSelectedFileType}
            pdfUrl={pdfUrl}
            setPdfUrl={setPdfUrl}
            extractedData={extractedData}
            setExtractedData={setExtractedData}
            originalExtractedData={originalExtractedData}
            setOriginalExtractedData={setOriginalExtractedData}
            documentId={documentId}
            setDocumentId={setDocumentId}
            uploadedFiles={uploadedFiles}
            setUploadedFiles={setUploadedFiles}
            pollingProgress={pollingProgress}
            setPollingProgress={setPollingProgress}
            pollingReferenceIds={pollingReferenceIds}
            setPollingReferenceIds={setPollingReferenceIds}
            progressComplete={progressComplete}
            setProgressComplete={setProgressComplete}
            loading={loading}
            setLoading={setLoading}
            error={error}
            setError={setError}
            onSaveDocument={handleSaveDocument}
            docType={docType}
            setDocType={setDocType}
            isDataChanged={isDataChanged}
            setIsDataChanged={setIsDataChanged}
            navigate={navigate}
            fetchThumbnail={fetchThumbnail}
            getThumbnailFilename={getThumbnailFilename}
            truncateFileName={truncateFileName}
          />
        )}
      </div>
    </ErrorBoundary>
  );
};

export default ViewAndUpload;